import React, { useEffect } from 'react';
import { Box, Button, Typography, Link, Paper } from '@mui/material';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { EventType } from '@azure/msal-browser';
import { LogButtonEventToGA } from '../../shared/googleAnalytics/GoogleAnalytics';
import { loginButtonClickedText } from '../../shared/googleAnalytics/GoogleAnalyticsConstants';
import { useRecordUserVisitMutation } from '../../data/api/endPointsSlice';

const Login = () => {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const [recordUserVisit] = useRecordUserVisitMutation();

  const handleLogin = async () => {
    LogButtonEventToGA(loginButtonClickedText);
    try {
      const accounts = instance.getAllAccounts();
      if (accounts.length === 0) {
        await instance.acquireTokenRedirect({
          scopes: ['a919164d-8b7c-43fb-8119-f1997d45ca4f/.default'],
        });
      }
      instance.setActiveAccount(accounts[0]);
      try {
        const response = await instance.acquireTokenSilent({
          scopes: ['a919164d-8b7c-43fb-8119-f1997d45ca4f/.default'],
        });
        localStorage.setItem('access_token', response.accessToken);
      } catch {
        return '';
      }
      
      // Track user visit after successful login
      try {
        const currentUser = accounts[0];
        await recordUserVisit({
          user_id: currentUser.localAccountId || currentUser.homeAccountId || '',
          user_name: currentUser.name || '',
          email: currentUser.username || '',
        });
      } catch (error) {
        console.error('Failed to record user visit:', error);
        // Don't block navigation if tracking fails
      }
      
      navigate('/home');
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  useEffect(() => {
    const checkAccounts = async () => {
      const accounts = instance.getAllAccounts();
      if (accounts.length > 0 && !instance.getActiveAccount()) {
        // Set the active account if not already set
        instance.setActiveAccount(accounts[0]);
        
        // Track user visit for auto-login
        try {
          const currentUser = accounts[0];
          await recordUserVisit({
            user_id: currentUser.localAccountId || currentUser.homeAccountId || '',
            user_name: currentUser.name || '',
            email: currentUser.username || '',
          });
        } catch (error) {
          console.error('Failed to record user visit:', error);
        }
        
        navigate('/home');
      }
    };

    // Check accounts on page load
    checkAccounts();

    // Listen for MSAL events
    const callbackId = instance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
        checkAccounts(); // Check accounts after successful login or token acquisition
      }
    });

    // Cleanup the event listener
    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, [instance, navigate, recordUserVisit]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f5f7fa',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        px: 2,
        py: 4,
      }}
    >
      <Paper
        elevation={4}
        sx={{
          width: '100%',
          maxWidth: 1200,
          minHeight: { xs: 'auto', md: '55vh' },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          borderRadius: '32px',
          overflow: 'hidden',
        }}
      >
        {/* Left Side */}
        <Box
          sx={{
            flex: 1,
            backgroundColor: '#fff',
            p: { xs: 4, md: 6 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Typography variant="h3" fontWeight={500} marginBottom={5} sx={{ fontSize: '2.5rem' }}>
            Wipro Agent Marketplace
          </Typography>

          <Button variant="outlined" size="large" sx={{ borderRadius: 2, px: 5, py: 1.5, mb: 2 }} onClick={handleLogin}>
            SSO LOGIN
          </Button>

          <Typography variant="body1" color="text.secondary" mb={1}>
            or
          </Typography>

          <Typography variant="body2">
            For access request mail us:
            <br />
            <Link href="mailto:ai.support@wipro.com" color="primary">
              ai.support@wipro.com
            </Link>
          </Typography>
        </Box>

        {/* Right Side */}
        <Box
          sx={{
            flex: 1,
            backgroundColor: '#eef1f7',
            p: { xs: 4, md: 6 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Typography variant="h5" color="primary" fontWeight="bold" gutterBottom>
            Transform Your Organization,
            <br /> Empower Your Future
          </Typography>

          <Typography variant="h6" fontWeight="bold" sx={{ mt: 3 }}>
            Introducing Wipro Agent Marketplace
          </Typography>

          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>
              • Chat and compare multiple chat agents
            </Typography>
            <Typography variant="body1" gutterBottom>
              • Communicate with documents
            </Typography>
            <Typography variant="body1">• More AI agents coming soon</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Footer */}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
        You agree to our{' '}
        <Link href="./termsAndPolicies/terms_and_conditions.html" underline="hover" target="_blank">
          Terms
        </Link>{' '}
        and have read our{' '}
        <Link href="./termsAndPolicies/privacy_policy.html" underline="hover" target="_blank">
          Privacy Policy
        </Link>
        .
      </Typography>
    </Box>
  );
};

export default Login;
